import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isKnockoutGame } from './tournamentCampaign';

dayjs.extend(utc);
dayjs.extend(timezone);

const EDIT_LOCK_HOURS = 4;
const DEFAULT_TIMEZONE = 'UTC';

/** Current moment in company timezone. */
export function getCompanyNow(timezone = DEFAULT_TIMEZONE) {
  return dayjs().tz(timezone || DEFAULT_TIMEZONE);
}

/** Parse stored game date/time (UTC) as an instant in company timezone. */
export function getMatchDateTime(game, timezone = DEFAULT_TIMEZONE) {
  const tz = timezone || DEFAULT_TIMEZONE;
  return dayjs.utc(`${game.date} ${game.time}`).tz(tz);
}

/** Saturday (6) or Sunday (0). */
export function isWeekendMatch(matchDateTime) {
  const dow = matchDateTime.day();
  return dow === 0 || dow === 6;
}

/** Start of the Friday before a weekend match (Sat → prev day, Sun → two days back). */
export function getPrecedingFriday(matchDateTime) {
  const matchDay = matchDateTime.startOf('day');
  const dow = matchDateTime.day();
  if (dow === 6) return matchDay.subtract(1, 'day');
  if (dow === 0) return matchDay.subtract(2, 'day');
  return null;
}

/**
 * When predictions open for a match.
 * - Today: start of match day
 * - Tomorrow: start of today (predict in advance)
 * - Weekend (Sat/Sun): start of preceding Friday
 */
export function getPredictionOpenTime(matchDateTime, now = dayjs()) {
  const today = now.startOf('day');
  const tomorrow = today.add(1, 'day');
  const matchDay = matchDateTime.startOf('day');

  if (matchDay.isSame(today, 'day')) {
    return matchDay;
  }
  if (matchDay.isSame(tomorrow, 'day')) {
    return today;
  }
  if (isWeekendMatch(matchDateTime)) {
    const friday = getPrecedingFriday(matchDateTime);
    return friday ? friday.startOf('day') : matchDay;
  }
  return matchDay.subtract(1, 'day').startOf('day');
}

/** Kick-off minus 4 hours — last moment predictions can be entered or edited. */
export function getEditDeadline(matchDateTime) {
  return matchDateTime.subtract(EDIT_LOCK_HOURS, 'hour');
}

/**
 * Window state for a single match.
 * countdownPhase: 'start' | 'edit' | 'closed'
 */
export function getPredictionWindowState(matchDateTime, now = dayjs()) {
  const predictionOpenTime = getPredictionOpenTime(matchDateTime, now);
  const editDeadline = getEditDeadline(matchDateTime);

  const beforeOpen = now.isBefore(predictionOpenTime);
  const beforeEditDeadline = now.isBefore(editDeadline);
  const canPredict = !beforeOpen && beforeEditDeadline;

  let countdownPhase = 'closed';
  let countdownTarget = null;

  if (beforeOpen) {
    countdownPhase = 'start';
    countdownTarget = predictionOpenTime;
  } else if (beforeEditDeadline) {
    countdownPhase = 'edit';
    countdownTarget = editDeadline;
  }

  return {
    canPredict,
    predictionOpenTime,
    editDeadline,
    countdownPhase,
    countdownTarget,
  };
}

const PREDICTOR_MATCH_LIMIT = 7;

function sortMatchesChronologically(matches) {
  return [...matches].sort(
    (a, b) => a.matchDateTime.valueOf() - b.matchDateTime.valueOf()
  );
}

/**
 * Next `limit` future matches for the dealer predictor (always shown).
 * All future knockout matches are always included, even when beyond `limit`.
 * On Friday, also includes any upcoming Sat/Sun matches for this weekend that
 * are not already in that list (prediction opens on preceding Friday).
 * Prediction availability per match is handled by getPredictionWindowState.
 * Items: { game, idx, matchDateTime }.
 */
export function getMatchesForPredictor(
  games,
  limit = PREDICTOR_MATCH_LIMIT,
  timezone = DEFAULT_TIMEZONE,
  now = getCompanyNow(timezone)
) {
  const futureMatches = games
    .map((game, idx) => ({
      game,
      idx,
      matchDateTime: getMatchDateTime(game, timezone),
    }))
    .filter(({ matchDateTime }) => matchDateTime.isAfter(now));

  const sorted = sortMatchesChronologically(futureMatches);
  const baseList = sorted.slice(0, limit);
  const includedIdx = new Set(baseList.map(({ idx }) => idx));

  const knockoutExtras = sorted.filter(
    ({ game, idx }) => !includedIdx.has(idx) && isKnockoutGame(game)
  );
  knockoutExtras.forEach(({ idx }) => includedIdx.add(idx));

  let extras = [...knockoutExtras];

  if (now.day() === 5) {
    const today = now.startOf('day');
    const weekendExtras = sorted.filter(({ matchDateTime, idx }) => {
      if (includedIdx.has(idx)) return false;
      if (!isWeekendMatch(matchDateTime)) return false;
      const friday = getPrecedingFriday(matchDateTime);
      return friday != null && friday.isSame(today, 'day');
    });
    extras = [...extras, ...weekendExtras];
  }

  if (extras.length === 0) {
    return baseList;
  }

  return sortMatchesChronologically([...baseList, ...extras]);
}

/**
 * Matches where kickoff has passed (complement of getMatchesForPredictor).
 * Items: { game, idx, matchDateTime } — oldest first.
 */
export function getPastMatches(games, timezone = DEFAULT_TIMEZONE, now = getCompanyNow(timezone)) {
  return games
    .map((game, idx) => ({
      game,
      idx,
      matchDateTime: getMatchDateTime(game, timezone),
    }))
    .filter(({ matchDateTime }) => !matchDateTime.isAfter(now))
    .sort((a, b) => a.matchDateTime.valueOf() - b.matchDateTime.valueOf());
}
